import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import { supabase } from '../../services/supabaseClient';
import { authService } from '../../services/authService';

/**
 * AuthCallback handles the redirect from OAuth providers and magic links.
 *
 * Uses onAuthStateChange listener instead of a timer to reliably detect
 * when Supabase has finished processing the URL hash/params. Includes
 * a 10-second safety timeout to avoid infinite loading states.
 */
export const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const handledRef = useRef(false);

  useEffect(() => {
    // Prevent double-handling in StrictMode
    if (handledRef.current) return;
    handledRef.current = true;

    let timeoutId: ReturnType<typeof setTimeout>;

    const handleAuthEvent = async (session: { access_token: string } | null) => {
      if (!session) {
        // No session from the callback -- redirect to login
        navigate('/login', { replace: true });
        return;
      }

      try {
        // Sync user with the backend (creates user record if first login)
        await authService.syncUser();
        navigate('/', { replace: true });
      } catch (syncErr) {
        console.error('AuthCallback: Failed to sync user with backend:', syncErr);
        setError('Failed to complete sign-in. Please try again.');
        setTimeout(() => navigate('/login', { replace: true }), 3000);
      }
    };

    // Listen for auth state changes from Supabase processing the URL hash
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        // Handle both SIGNED_IN and INITIAL_SESSION events
        // INITIAL_SESSION fires when there's already a session from the URL hash
        // SIGNED_IN fires when a new sign-in completes
        if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') {
          clearTimeout(timeoutId);
          await handleAuthEvent(session);
        }
      }
    );

    // Safety timeout: if no auth event fires within 10 seconds, redirect to login
    timeoutId = setTimeout(() => {
      console.warn('AuthCallback: Timed out waiting for auth event');
      setError('Sign-in timed out. Please try again.');
      setTimeout(() => navigate('/login', { replace: true }), 3000);
    }, 10000);

    // Cleanup on unmount
    return () => {
      clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, [navigate]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
      }}
    >
      {error ? (
        <Alert severity="error" sx={{ maxWidth: 400 }}>
          {error}
        </Alert>
      ) : (
        <>
          <CircularProgress size={48} sx={{ mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            Completing sign in...
          </Typography>
        </>
      )}
    </Box>
  );
};

export default AuthCallback;
